import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import i18next from 'i18next';
import { useHistory, useRouteMatch } from 'react-router';

import { DATA_TEST_ID } from '~constants/tests';
import { CLIENT_PAGES } from '~constants/pages';
import { arrayHasLength } from '~utils/array';
import ModalActions from '~redux/Modal/actions';
import { MODALS } from '~redux/Modal/constants';
import CustomModal from '~app/components/CustomModal';
import Text from '~components/Text';
import { formatPrice } from '~utils/number';
import { isSmallDevice } from '~utils/screenSize';
import useOrderDetails from '~hooks/useOrderDetail';

import ItemsCart from './components/ItemsCart';
import styles from './styles.module.scss';

function MiniCartModal() {
  const dispatch = useDispatch();
  const { cart: cartItems } = useSelector((state) => state.cart);
  const { subtotal } = useOrderDetails();
  const history = useHistory();
  const isOpen = useSelector((state) => state.modal[`${MODALS.MINI_CART}ModalIsOpen`]);
  const currentSubsidiary = useSelector((state) => state.searchStore?.currentSubsidiary);
  const closeMiniCartMenu = () => {
    dispatch(ModalActions.closeModal(MODALS.MINI_CART));
  };
  const goToCheckout = () => {
    closeMiniCartMenu();
    history.push(CLIENT_PAGES.CHECKOUT.basePath);
  };
  const openChangeAddressModal = useCallback(() => {
    dispatch(ModalActions.openModal(MODALS.DELIVERY_OR_PICKUP));
  }, [dispatch]);

  useEffect(() => {
    if (cartItems.length === 1 && !currentSubsidiary) {
      openChangeAddressModal();
    }
  }, [cartItems, currentSubsidiary, openChangeAddressModal]);

  const isCheckout = useRouteMatch(CLIENT_PAGES.CHECKOUT.basePath);

  return (
    <CustomModal
      modal={MODALS.MINI_CART}
      className={`${styles.modal} full-height`}
      overlayClassName="row end fullscreen"
      hideCloseButton
      onClose={closeMiniCartMenu}
      isOpen={isOpen}>
      <div className={cn('column', styles.miniCartInner)}>
        <div className={styles.miniCartInnerHeader}>
          <div
            data-testid={DATA_TEST_ID.closeMiniCart}
            className={styles.closeButton}
            onClick={closeMiniCartMenu}>
            <i className="icon-arrow-left" />
          </div>
          <div className={`row ${styles.title}`}>{i18next.t('MiniCartModal:headerTitle')}</div>
        </div>
        <div
          className={cn(styles.miniCartInnerBody, {
            [styles.noItems]: !arrayHasLength(cartItems),
            [styles.withItems]: arrayHasLength(cartItems)
          })}>
          <ItemsCart
            items={cartItems}
            productModalUrl={isCheckout ? CLIENT_PAGES.CHECKOUT.basePath : CLIENT_PAGES.HOME.basePath}
          />
        </div>
      </div>
      {arrayHasLength(cartItems) && !isCheckout && (
        <div className={`row m-top-2 ml-1 ${styles.modalBottom}`}>
          <button
            type="button"
            onClick={goToCheckout}
            className={`button primary row space-around middle full-width ${styles.button}`}
            data-testid={DATA_TEST_ID.goToCheckout}>
            <div className="row space-around full-width">
              <Text className="font-size-15 body-font text-uppercase text-white bold m-right-1">
                {isSmallDevice() ? i18next.t('MiniCartModal:goToCart') : i18next.t('MiniCartModal:buy')}
              </Text>
              <Text className="font-size-15 body-font text-uppercase text-white bold">
                {formatPrice(subtotal)}
              </Text>
            </div>
          </button>
        </div>
      )}
    </CustomModal>
  );
}

export default MiniCartModal;
